import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen,

} from '../../utils/ui_utils'
import moment from 'moment'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useDispatch } from 'react-redux'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useFetchWaiters } from '../../modules/waiters/waiterHooks'
import { useFetchWaiterMonthWiseReport } from '../../modules/reports/reportsHooks.ts'
import { clearWaiterMonthWiseState } from '../../modules/reports/reportsSlice.ts'
import * as XLSX from 'xlsx'
import { BarChart } from '../../components/Chart'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'

var JSONbig = require('json-bigint')({ useNativeBigInt: true })

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const WaiterMonthWiseReport = () => {
  // Common states
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  // Commission mode filter: 0 = All, 1 = Item Sale, 2 = Value Sale
  const [commissionMode, setCommissionMode] = useState(0)
  const [selectedWaiter, setSelectedWaiter] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [selectedYear, setSelectedYear] = useState(moment().year())
  const [goToPageInput, setGoToPageInput] = useState('')

  // Year options: current year and 5 prior years (newest first)
  const yearOptions = Array.from({ length: 6 }, (_, i) => moment().year() - i)

  // Report data states
  const [monthWiseData, setMonthWiseData] = useState([])
  const [monthWiseAggregate, setMonthWiseAggregate] = useState(null)
  const [reportVisibility, setReportVisibility] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Commission',
        data: [],
        backgroundColor: ['#e0218a'],
        barThickness: 30
      }
    ]
  })

  const history = useNavigate()
  const dispatch = useDispatch()
  const { outletFetch, outletFetchData, outletFetchLoading } = useFetchOutlet()
  const { fetchWaiters: fetchWaitersApi, fetchWaitersData, fetchWaitersLoading } = useFetchWaiters()
  const { waiterMonthWiseReportLoading, waiterMonthWiseReportData, waiterMonthWiseReportFetch } = useFetchWaiterMonthWiseReport()

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity = 'success') => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchReportData(value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const pageCount = (total) => {
    let count = Math.ceil(total / limit)
    setPageCount(count)
  }

  // Fetch outlets
  const fetchOutlets = async () => {
    await outletFetch({ pageNumber: 1, limit: 200 })
  }

  // Fetch waiters for selected outlet
  const fetchWaiters = async (outletId) => {
    if (isEmpty(outletId)) return
    await fetchWaitersApi({ outlet_id: outletId, pageNumber: 1, limit: 1000 })
  }

  // Fetch report data
  // Year-only filter: range is Jan 1 — Dec 31 of the selected year.
  const fetchReportData = async (pageNum = page) => {
    if (isEmpty(selectedWaiter)) {
      toastMessage('Please select a waiter', 'error')
      return
    }

    setHasFetched(true)
    await waiterMonthWiseReportFetch({
      from_date: `${selectedYear}-01-01`,
      to_date: `${selectedYear}-12-31`,
      waiter_id: selectedWaiter,
      outlet_id: selectedOutlet,
      pageNumber: pageNum,
      limit,
      commission_mode: commissionMode
    })
  }

  // Initialize
  useEffect(() => {
    businessCheck(history)
    fetchOutlets()
    return () => {
      dispatch(clearWaiterMonthWiseState())
    }
  }, [])

  // Update selected outlet when outlet data loads
  useEffect(() => {
    if (outletFetchData?.data && outletFetchData.data.length > 0 && isEmpty(selectedOutlet)) {
      const firstOutlet = outletFetchData.data.find(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
      if (firstOutlet) setSelectedOutlet(firstOutlet._id)
    }
  }, [outletFetchData])

  // Update selected waiter when waiter data loads
  useEffect(() => {
    if (fetchWaitersData?.data && fetchWaitersData.data.length > 0 && isEmpty(selectedWaiter)) {
      setSelectedWaiter(fetchWaitersData.data[0]._id)
    }
  }, [fetchWaitersData])

  // Fetch waiters when outlet changes
  useEffect(() => {
    if (!isEmpty(selectedOutlet)) {
      fetchWaiters(selectedOutlet)
      setSelectedWaiter('') // Reset waiter selection
    }
  }, [selectedOutlet])

  // Update month-wise report data
  useEffect(() => {
    if (waiterMonthWiseReportData) {
      if (waiterMonthWiseReportData.data && waiterMonthWiseReportData.data.length > 0) {
        setMonthWiseData(waiterMonthWiseReportData.data)
        setMonthWiseAggregate(waiterMonthWiseReportData.aggregate)
        pageCount(waiterMonthWiseReportData.total)
        setReportVisibility(true)

        // Update graph data
        setGraphData({
          labels: waiterMonthWiseReportData.data.map((data) =>
            `${moment().month(data.Month - 1).format('MMM')} ${data.Year}`
          ),
          datasets: [
            {
              label: 'Commission',
              data: waiterMonthWiseReportData.data.map((data) => data.Commission || 0),
              backgroundColor: ['#e0218a'],
              barThickness: 30
            }
          ]
        })
      } else {
        setMonthWiseData([])
        setMonthWiseAggregate(null)
        setReportVisibility(false)
        setGraphData({
          labels: [],
          datasets: [
            {
              label: 'Commission',
              data: [],
              backgroundColor: ['#e0218a'],
              barThickness: 30
            }
          ]
        })
      }
    }
  }, [waiterMonthWiseReportData])

  // Helper: convert Comm_Mode (int or string) to a readable label
  const modeLabel = (cm) => {
    const v = cm === null || cm === undefined ? null : Number(cm)
    if (v === 1) return 'Item Sale'
    if (v === 2) return 'Value Sale'
    return ''
  }

  // Helper: green/blue dot color based on Comm_Mode
  const modeDotColor = (cm) => {
    const v = cm === null || cm === undefined ? null : Number(cm)
    if (v === 1) return '#2e7d32' // green
    if (v === 2) return '#1565c0' // blue
    return null
  }

  // Export to Excel
  const exportToExcel = () => {
    const isAllMode = Number(commissionMode) === 0
    let data = monthWiseData.map((row) => ({
      ...row,
      MonthYear: `${row.Month}/${row.Year}`,
      Mode_Label: modeLabel(row?.Comm_Mode)
    }))
    let filename = 'Waiter_MonthWise_Report'
    let headers = [
      { field: 'MonthYear', title: 'Month' },
      { field: 'Tax', title: 'Tax (GST)' },
      { field: 'CESS', title: 'CESS' },
      { field: 'Discount', title: 'Discount' },
      { field: 'TotalAmount', title: 'Total Amount' },
      { field: 'Commission', title: 'Commission' },
      ...(isAllMode ? [{ field: 'Mode_Label', title: 'Mode' }] : [])
    ]

    // No records — show a single 'No records found' row instead of an empty sheet
    if (data.length === 0) {
      data.push({ MonthYear: 'No records found', __label: true })
    }

    if (monthWiseAggregate) {
      data.push(
        {
          MonthYear: 'TOTAL',
          Tax: monthWiseAggregate.TotalGST?.toFixed(2) ?? '-',
          CESS: monthWiseAggregate.TotalCess?.toFixed(2) ?? '-',
          Discount: monthWiseAggregate.TotalDisc?.toFixed(2) ?? '-',
          TotalAmount: monthWiseAggregate.Total?.toFixed(2) ?? '-',
          Commission:
            monthWiseAggregate.TotalCommission?.toFixed(2) ?? '-'
        }
      )
    }

    try {
      const ws = XLSX.utils.json_to_sheet(
        data.map((row) => {
          const newRow = {}
          headers.forEach((h) => {
            if (row.__label) {
              newRow[h.title] = h.field === 'MonthYear' ? (row.MonthYear ?? '') : ''
            } else {
              newRow[h.title] = row[h.field] ?? '-'
            }
          })
          return newRow
        })
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Report')
      XLSX.writeFile(wb, `${filename}_${moment().format('YYYY-MM-DD')}.xlsx`)
      toastMessage('Report exported successfully')
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      toastMessage('Error exporting report', 'error')
    }
  }


  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month/Year',
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Commission (₹)',
          font: {
            weight: 'bold'
          }
        },
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      datalabels: {
        display: true,
        color: 'black',
        formatter: Math.round,
        anchor: 'end',
        offset: -20,
        align: 'start'
      }
    }
  }

  // Refresh functionality
  const refreshFunction = () => fetchReportData(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } = useReportRefresh(
    refreshFunction,
    [page, selectedOutlet, selectedWaiter, commissionMode, selectedYear]
  )

  // Auto-fetch report when filters change
  useEffect(() => {
    if (!isEmpty(selectedWaiter) && !isEmpty(selectedOutlet)) {
      setPage(1)
      fetchReportData(1)
    }
  }, [selectedOutlet, selectedWaiter, commissionMode, selectedYear])

  return (
    <Box sx={{ p: 2 }}>
      <Helmet title="Waiter Month-Wise Report" />

      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Waiter Month-Wise Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={waiterMonthWiseReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mb: 3 }} />

      {/* Filters */}
      <MainCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Outlet Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Outlet</InputLabel>
                <Select
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                  label="Outlet"
                  disabled={outletFetchLoading}
                >
                  {(outletFetchData?.data || [])
                    .filter((o) => String(o?._id) !== '0' && o?.name !== 'All Outlets')
                    .map((outlet) => (
                    <MenuItem key={outlet._id} value={outlet._id}>
                      {outlet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Commission Mode Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Commission Mode</InputLabel>
                <Select
                  value={commissionMode}
                  onChange={(e) => setCommissionMode(e.target.value)}
                  label="Commission Mode"
                >
                  <MenuItem value={0}>All</MenuItem>
                  <MenuItem value={1}>Item Sale</MenuItem>
                  <MenuItem value={2}>Value Sale</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Waiter Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Waiter</InputLabel>
                <Select
                  value={selectedWaiter}
                  onChange={(e) => setSelectedWaiter(e.target.value)}
                  label="Waiter"
                  disabled={fetchWaitersLoading || (fetchWaitersData?.data?.length || 0) === 0}
                >
                  {(fetchWaitersData?.data || []).map((waiter) => (
                    <MenuItem key={waiter._id} value={waiter._id}>
                      {waiter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            {/* Year Selector */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Year"
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </MainCard>

      {/* Report Display */}
      {waiterMonthWiseReportLoading && (
        <Grid container spacing={10} direction={'column'} sx={{ paddingTop: 5 }}>
          <Grid item>
            <Grid container rowSpacing={4.5} columnSpacing={10} sx={{ mt: 1 }}>
              <Grid item xs sm md={8}>
                <MainCard content={false} sx={{ mt: 1.5 }}>
                  <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                    <ListTypeShimmer />
                  </Box>
                </MainCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!waiterMonthWiseReportLoading && reportVisibility && (
        <>

          {/* Export Button */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Export to Excel" placement="top" arrow>
              <IconButton variant="contained" onClick={exportToExcel} size="large">
                <CloudUploadIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Graph */}
          {graphData.labels.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Commission Trend
                </Typography>
                <Box sx={{ height: '400px' }}>
                  <BarChart chartData={graphData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Mode legend (only when 'all' is selected) */}
          {Number(commissionMode) === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  Item Sale
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1565c0' }} />
                <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600 }}>
                  Value Sale
                </Typography>
              </Box>
            </Box>
          )}

          {/* Report Table */}
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Month</StyledTableCell>
                  <StyledTableCell>Year</StyledTableCell>
                  <StyledTableCell align="right">Tax (GST)</StyledTableCell>
                  <StyledTableCell align="right">CESS</StyledTableCell>
                  <StyledTableCell align="right">Discount</StyledTableCell>
                  <StyledTableCell align="right">Total Amount</StyledTableCell>
                  <StyledTableCell align="right">Commission</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {monthWiseAggregate && (
                  <TableRow sx={{ backgroundColor: '#ECF9FF' }}>
                    <StyledTableCell sx={{ fontWeight: 'bold' }}>TOTAL:</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }}>-</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {monthWiseAggregate.TotalGST?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {monthWiseAggregate.TotalCess?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {monthWiseAggregate.TotalDisc?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {monthWiseAggregate.Total?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {monthWiseAggregate.TotalCommission?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                  </TableRow>
                )}
                {monthWiseData.map((row, i) => {
                  const dotColor = modeDotColor(row?.Comm_Mode)
                  return (
                  <StyledTableRow key={i}>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {dotColor && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                        )}
                        <span>
                          {moment()
                            .month(row.Month - 1)
                            .format('MMMM')}
                        </span>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{row.Year}</StyledTableCell>
                    <StyledTableCell align="right">{row.Tax?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.CESS?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Discount?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.TotalAmount?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Commission?.toFixed(2) ?? '0.00'}</StyledTableCell>
                  </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagecount > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                mt: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Pagination
                  count={pagecount}
                  page={page}
                  color="secondary"
                  size="small"
                  boundaryCount={2}
                  siblingCount={0}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: 'white',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Jump to
                </Typography>
                <TextField
                  size="small"
                  value={goToPageInput}
                  onChange={handleGoToPageChange}
                  onKeyPress={handleGoToPageKeyPress}
                  placeholder="Page"
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        borderColor: 'grey.300',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '1.5px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.1)'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      textAlign: 'center',
                      fontWeight: 500,
                      padding: '7px 8px',
                      height: '20px',
                      lineHeight: '20px'
                    }
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  of {pagecount}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleGoToPageSubmit}
                  disabled={
                    !goToPageInput ||
                    parseInt(goToPageInput, 10) < 1 ||
                    parseInt(goToPageInput, 10) > pagecount
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 0.75,
                    height: '34px',
                    lineHeight: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {!waiterMonthWiseReportLoading && hasFetched && !reportVisibility && <ShowNoRecordsFound />}

      {/* Snackbar */}
      <Snackbar
        open={snackbar}
        autoHideDuration={SnackBarHideDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleSnackbarClose}
        TransitionComponent={SnackBarTransition}
      >
        <Alert onClose={handleSnackbarClose} severity={severity}>
          {PopUpMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default WaiterMonthWiseReport
